import axios from "axios"
import JwtService from "@/services/jwt.service"
import store from '@/store'
import LangHelper from "@/services/lang.helper"
import BranchHelper from "@/services/branch.helper"

const ApiService = {
  init(i18n, router, app) {
    // axios.defaults.baseURL = process.env.VUE_APP_API_PROXY_URL
    axios.defaults.baseURL = process.env.VUE_APP_API_URL
    axios.interceptors.request.use((config) => {
      config.headers['Accept-Language'] = LangHelper.getLowcaseLanguage()
      let token = JwtService.getToken()
      config.headers['Authorization'] = token ? `Bearer ${token}` : `${process.env.VUE_APP_API_TOKEN}`
      if(['auth/login', 'auth/password/forgot', 'auth/password/reset'].includes(config.url)) {
        config.headers['Authorization'] = process.env.VUE_APP_API_TOKEN
      }
      // remembers which token this request was actually sent with, so a 401 that comes back
      // after the user has since logged into a different session (a new token already saved)
      // can be recognized as belonging to the old, superseded session - see response handler
      config.__authTokenAtDispatch = token
      let loader = config.loader
      store.commit('setLoading',typeof loader !== 'undefined' ? loader : true)
      config.headers['From'] = BranchHelper.getBranch()
      return config
    }, error => Promise.reject(error))

    axios.interceptors.response.use((response) => {
      if(response.config.loader !== false) { // not to interfere with other loader
        store.commit('setLoading',false)
      }
      const appVersion = response.headers.app_version
      if(appVersion) JwtService.saveAppVersion(appVersion)
      return response
    }, function (error) {
      store.commit('setLoading',false)
      const status = error.response ? error.response.status : 500
      console.log(error.response)
      if(status === 422) {
        console.log(error.response.data)
        if(error.response.data.errors.show_message) {
          app.noty.error(error.response.data.errors.show_message[0])
        }
        store.commit('setValidationErrors', error.response.data.errors)
      }
      if(status === 405 || status === 403) {
        app.noty.error(i18n.global.t('not_enough_rights_for_this_operation'))
      }
      if(status === 406) {
        app.noty.error(i18n.global.t('your_tariff_plan_is_not_paid'))
      }
      if(status === 409) {
        app.noty.error(i18n.global.t('clinic_is_blocked'))
      }
      if(status === 500 && error?.response) {
        app.noty.error(i18n.global.t('server_error'))
      }
      if(status === 503) {
        router.push('/maintenance')
      }
      if(status === 423 && error.response.data.must_change_password) {
        // safety net for a stray call to some other endpoint made just before the
        // login/initial-driven redirect below takes effect - login() and the app's
        // initial bootstrap already handle this gracefully via a 200 response
        store.commit('setMustChangePassword', { user: store.state.auth.user })
        if(router.currentRoute.value.name !== 'ChangePassword') {
          router.push('/change-password')
        }
      }
      if(status === 401) {
        // only log out if this request was actually sent under the session that's still
        // active - a request dispatched under a since-superseded token (e.g. the user logged
        // into a different clinic while this request was still in flight) can 401 well after
        // the new, valid session's token has already been saved, and must not tear it down
        const requestMatchesCurrentSession = error.config?.__authTokenAtDispatch === JwtService.getToken()
        if(requestMatchesCurrentSession) {
          store.dispatch('logout').finally(() => router.push('/login'))
        }
      }
      if(status === 400 && error.response.data.message) {
        app.noty.error(error.response.data.message?.message || error.response.data.message)
      }
      return Promise.reject(error)
    })
  },

  get(resource, params = {}, headers = {}) {
    let p = { params: params }
    p = {...p, ...headers }
    return axios.get(resource, p)
  },

  post(resource, params = {}, headers = {}, config = {}) {
    headers['Accept'] = 'application/json'
    config.headers = headers
    return axios.post(`${resource}`, params, config)
  },

  patch(resource, params = {}, headers = {}, config = {}) {
    headers['Accept'] = 'application/json'
    config.headers = headers
    return axios.patch(`${resource}`, params, config)
  },

  delete(resource, headers = {}, config = {}) {
    headers['Accept'] = 'application/json'
    config.headers = headers
    return axios.delete(`${resource}`, config)
  },
};

export default ApiService;
